import { format } from 'date-fns';
import {
  ChangeEvent,
  FunctionComponent,
  KeyboardEvent,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_INPUT_FORMAT, TIME_INPUT_FORMAT } from '../../../constants';
import { getCssVar } from '../../../utils';
import { Button, ButtonType } from '../../Button';
import { IconLabel } from '../../IconLabel';
import { InputField } from '../../InputField';
import { CalendarGrid } from '../CalendarGrid';
import {
  buildQuickTimes,
  getInitialScheduleDate,
  getScheduleError,
  maskDateInput,
  maskTimeInput,
  parseDateInput,
  parseTimeInput,
} from '../helpers';

import classNames from 'classnames';
import classes from './SchedulePicker.module.scss';

const DATE_INPUT_KEYS = /[\d.]/;

const TIME_INPUT_KEYS = /[\d:]/;

interface RejectKeysArgs {
  event: KeyboardEvent<HTMLInputElement>;
  allowed: RegExp;
}

const rejectKeysOutside = ({ event, allowed }: RejectKeysArgs) => {
  if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !allowed.test(event.key)) {
    event.preventDefault();
  }
};

interface SchedulePickerProps {
  time: Date | null;
  onConfirm: (value: Date) => void;
  onClear: () => void;
  subTitle?: string;
  summaryLabel?: string;
  confirmLabel?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
}

export const SchedulePicker: FunctionComponent<SchedulePickerProps> = memo(
  ({ time, onConfirm, onClear, subTitle, summaryLabel, confirmLabel, minDate, maxDate }) => {
    const { t } = useTranslation();

    const [initialDate] = useState(() => getInitialScheduleDate({ time, minDate, maxDate }));

    const [dateText, setDateText] = useState(() => format(initialDate, DATE_INPUT_FORMAT));

    const [timeText, setTimeText] = useState(() => format(initialDate, TIME_INPUT_FORMAT));

    const [viewDate, setViewDate] = useState(initialDate);

    const selectedDay = useMemo(() => parseDateInput(dateText), [dateText]);

    const draftDate = useMemo(() => {
      if (!selectedDay) {
        return null;
      }

      return parseTimeInput({ text: timeText, referenceDate: selectedDay });
    }, [selectedDay, timeText]);

    const errorKey = useMemo(
      () =>
        getScheduleError({
          date: draftDate,
          isComplete:
            dateText.length === DATE_INPUT_FORMAT.length &&
            timeText.length === TIME_INPUT_FORMAT.length,
          minDate,
          maxDate,
        }),
      [draftDate, dateText, timeText, minDate, maxDate]
    );

    const selectDay = useCallback((day: Date) => setDateText(format(day, DATE_INPUT_FORMAT)), []);

    const selectQuickTime = useCallback((date: Date) => {
      setDateText(format(date, DATE_INPUT_FORMAT));
      setTimeText(format(date, TIME_INPUT_FORMAT));
      setViewDate(date);
    }, []);

    const quickTimes = useMemo(
      () =>
        buildQuickTimes({
          selectedDay,
          relativeLabel: t('schedule.in-one-hour'),
          minDate,
          maxDate,
        }),
      [selectedDay, t, minDate, maxDate]
    );

    const dateKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) =>
        rejectKeysOutside({ event, allowed: DATE_INPUT_KEYS }),
      []
    );

    const timeKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) =>
        rejectKeysOutside({ event, allowed: TIME_INPUT_KEYS }),
      []
    );

    const dateChange = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
      const nextDateText = maskDateInput({
        raw: target.value,
        isAppending: target.selectionStart === target.value.length,
      });

      setDateText(nextDateText);

      const parsed = parseDateInput(nextDateText);

      if (parsed) {
        setViewDate(parsed);
      }
    }, []);

    const timeChange = useCallback(
      ({ target }: ChangeEvent<HTMLInputElement>) =>
        setTimeText(
          maskTimeInput({
            raw: target.value,
            isAppending: target.selectionStart === target.value.length,
          })
        ),
      []
    );

    const confirm = useCallback(() => {
      if (!draftDate) {
        return;
      }

      onConfirm(draftDate);
    }, [draftDate, onConfirm]);

    const renderError = useMemo(() => {
      if (!errorKey) {
        return null;
      }

      return <div className={classes['schedule-picker__error']}>{t(errorKey)}</div>;
    }, [errorKey, t]);

    const renderQuickTimes = useMemo(() => {
      if (!quickTimes.length) {
        return null;
      }

      const isChipSelected = (date: Date) =>
        dateText === format(date, DATE_INPUT_FORMAT) &&
        timeText === format(date, TIME_INPUT_FORMAT);

      return (
        <div className={classes['schedule-picker__quick-times']}>
          <span className={classes['schedule-picker__quick-times-label']}>
            {t('schedule.quick-times')}
          </span>

          <div className={classes['schedule-picker__chips']}>
            {quickTimes.map(({ label, date }) => (
              <button
                key={date.toISOString()}
                type={'button'}
                className={classNames(classes['schedule-picker__chip'], {
                  [classes['schedule-picker__chip--selected']]: isChipSelected(date),
                })}
                onClick={() => selectQuickTime(date)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );
    }, [quickTimes, t, timeText, dateText, selectQuickTime]);

    const renderSummary = useMemo(() => {
      if (!draftDate || errorKey) {
        return null;
      }

      return (
        <span className={classes['schedule-picker__summary']}>
          {summaryLabel ?? t('schedule.selected')}{' '}
          <span className={classes['schedule-picker__summary-value']}>
            {format(draftDate, 'EEE, d MMM · HH:mm')}
          </span>
        </span>
      );
    }, [draftDate, errorKey, summaryLabel, t]);

    return (
      <div className={classes['schedule-picker']}>
        {subTitle && <p className={classes['schedule-picker__subtitle']}>{subTitle}</p>}

        <div className={classes['schedule-picker__fields']}>
          <InputField
            value={dateText}
            label={t('schedule.date')}
            placeholder={DATE_INPUT_FORMAT.toLowerCase()}
            maxLength={DATE_INPUT_FORMAT.length}
            onChange={dateChange}
            onKeyDown={dateKeyDown}
            className={classes['schedule-picker__field']}
            icon={
              <IconLabel
                iconId={'calendar-add'}
                iconSize={18}
                color={getCssVar('--input-icon-color')}
                singleColor
                nonClickable
              />
            }
            error={Boolean(errorKey)}
          />

          <InputField
            value={timeText}
            label={t('common.time')}
            placeholder={TIME_INPUT_FORMAT.toLowerCase()}
            maxLength={TIME_INPUT_FORMAT.length}
            onChange={timeChange}
            onKeyDown={timeKeyDown}
            className={classes['schedule-picker__field']}
            icon={
              <IconLabel
                iconId={'clock'}
                iconSize={18}
                color={getCssVar('--input-icon-color')}
                singleColor
                nonClickable
              />
            }
            error={Boolean(errorKey)}
          />
        </div>

        {renderError}

        <CalendarGrid
          viewDate={viewDate}
          setViewDate={setViewDate}
          selectedDay={selectedDay}
          onSelectDay={selectDay}
          minDate={minDate}
          maxDate={maxDate}
        />

        {renderQuickTimes}

        <div className={classes['schedule-picker__footer']}>
          {renderSummary}

          <div className={classes['schedule-picker__actions']}>
            <Button type={ButtonType.secondary} label={t('common.clear')} onClick={onClear} />

            <Button
              type={ButtonType.primary}
              label={confirmLabel ?? t('schedule.confirm')}
              onClick={confirm}
              disabled={!draftDate || Boolean(errorKey)}
            />
          </div>
        </div>
      </div>
    );
  }
);
